import { BaseError } from '@zurich-framework/core/errors';
import { generateUuid } from '@zurich-framework/core/utils';
import { isServerSide } from '../common';
import { NextAppRoot } from '../components';
import { createErrorObject as createErrorObjectNext } from '../errors';
/**
 * Framework entry point for the next.js-based application
 *
 * This high-order function will extend the passed App component with base framework functionality:
 * - config
 * - error handling
 * - logging (using a business process id as required by the backend)
 * - api fetching and api type generation
 * - authentication and sessions
 * - Google Tag Manager support
 *
 * The appInstance object represents app-specific (mostly client-side) implementations to customize a next.js app.
 * By passing the createAppInstance factory function the appInstance object is created and its properties and callbacks
 * are used throughout the framework for customizations.
 *
 * The framework has two primary goals:
 * - allow scaling of the code base to multiple, independent web applications with a minimum amount of duplicate code
 * - ensure type-safety for all next.js, react and api functionality
 */
export const withNextApp = (createAppInstance, AppComponent) => (props) => {
    const { pageProps: { __CONFIG__: config = null, __SESSION__: session = null, __PUBLIC__: isPagePublic = false, __SESSION_DISABLED__: sessionDisabled = false, __BUSINESSPROCESSID__: businessProcessId = generateUuid(), __ERROR__: serializedError = null, __TRANSLATIONS__: translationProps, ...pageProps }, Component, router, } = props;
    const { locale } = props.router;
    if (!locale)
        throw new Error('Invalid locale without fallback');
    if (!config)
        throw new Error('Invalid configuration object');
    // create app instance using the passed factory function from the specific web application
    // this will inject the app-specific changes into the framework for easy customization
    const appInstance = createAppInstance({
        session,
        config,
        pageProps: props.pageProps,
    });
    const error = BaseError.fromJSON(serializedError, (className) => appInstance.createErrorObject?.(className) ??
        createErrorObjectNext(className));
    // display errors in console
    if (!isServerSide() && config?.debug && error) {
        console.error('🚩 Errors:', error);
    }
    AppComponent.displayName = 'AppComponent';
    // add translation
    return NextAppRoot({
        context: {
            appInstance,
            config,
            locale,
            session,
            sessionDisabled,
            isPagePublic,
            businessProcessId,
            error,
        },
        AppComponent,
        pageProps: pageProps,
        translationProps,
        router,
        Component,
    });
};
export default withNextApp;
